import React, { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";

import UserUpdateModal from "./UserUpdateModal";
import CancelModal from "./CancelSubscriptionModal";
import DeleteModal from "./DeleteSubscriberModal";
import AddSubscriberModal from "./AddSubscriberModal";
import ResubscribeInviteModal from "./ResubscribeInviteModal";
import ArchivedDetailsModal from "./ArchivedDetailsModal";
import Filters from "./Filters";
import CopyText from "./CopyText";
import ExternalLink from "../../svg/ExternalLink";
import MoreVertical from "../../svg/MoreVertical";
import Alert from "../Alert";
import { formatAddress, sendGetRequest } from "../../utils";

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const STATUS_OPTIONS = ["0", "1", "2"];
const SORT_OPTIONS = ["name", "id", "city", "street_name"];
const VIEW_OPTIONS = ["members", "archived"];
const SEARCH_FIELDS = ["name", "phone_number", "email", "city"];

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-CA") : "—";

function Customer() {
  const [data, setData] = useState([]);
  const [archivedData, setArchivedData] = useState([]);
  const [archivedUser, setArchivedUser] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [user, setUser] = useState(null);
  const [isCancel, setIsCancel] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isAdd, setIsAdd] = useState(false);
  const [isInvite, setIsInvite] = useState(false);
  const [error, setError] = useState(null);
  // Filter, sort, search, page and page size live in the URL
  // (?status=1&sort=city&q=sutton&page=2&rows=50) so a refresh or a
  // shared link shows the same list
  const [searchParams, setSearchParams] = useSearchParams();
  const pick = (name, options) =>
    options.includes(searchParams.get(name))
      ? searchParams.get(name)
      : options[0];
  const view = pick("view", VIEW_OPTIONS);
  const isArchivedView = view === "archived";
  const filterByStatus = pick("status", STATUS_OPTIONS);
  const sortBy = pick("sort", SORT_OPTIONS);
  const searchQuery = searchParams.get("q") || "";
  const page = Math.max(0, (parseInt(searchParams.get("page"), 10) || 1) - 1);
  const rowsParam = Number(searchParams.get("rows"));
  const rowsPerPage = ROWS_PER_PAGE_OPTIONS.includes(rowsParam)
    ? rowsParam
    : ROWS_PER_PAGE_OPTIONS[0];

  // changes: { param: value }; an empty value removes the param, which
  // keeps default settings out of the URL
  const updateParams = (changes, options) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      for (const [name, value] of Object.entries(changes)) {
        if (value) next.set(name, value);
        else next.delete(name);
      }
      return next;
    }, options);
  };

  const setPagination = (newPage, newRowsPerPage) =>
    updateParams({
      page: newPage > 0 ? String(newPage + 1) : "",
      rows:
        newRowsPerPage !== ROWS_PER_PAGE_OPTIONS[0]
          ? String(newRowsPerPage)
          : "",
    });
  const [isPrinting, setIsPrinting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const doFetchCustomers = () => {
    sendGetRequest("customers", true)
      .then((res) => res.json())
      .then((data) => {
        // An expired login or a server error comes back as { errMessage }
        if (!Array.isArray(data)) {
          setError(data.errMessage || "Failed to load customers.");
          return;
        }
        setError(null);
        setData(data);
        setFilteredData(data);
      })
      .catch(() => setError("Failed to load customers."));

    // Deleting a member moves them to the archive, so refresh both lists
    sendGetRequest("archived-customers", true)
      .then((res) => res.json())
      .then((archived) => {
        if (Array.isArray(archived)) setArchivedData(archived);
      })
      .catch(() => {});
  };

  useEffect(() => {
    doFetchCustomers();
  }, []);

  // Printing must include every row, not just the page on screen
  useEffect(() => {
    const beforePrint = () => flushSync(() => setIsPrinting(true));
    const afterPrint = () => setIsPrinting(false);
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, []);

  const doSetUser = (user) => setUser(user);

  const handleCloseMenu = () => setMenuAnchor(null);

  // Opens the modal for the row whose action menu was used
  const doMenuAction = (setIsOpen) => {
    doSetUser(menuRow);
    setIsOpen(true);
    handleCloseMenu();
  };

  const handleCloseUpdateModal = useCallback(() => {
    setUser(null);
    setIsUpdate(false);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setUser(null);
    setIsCancel(false);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setUser(null);
    setIsDelete(false);
  }, []);

  const handleCloseInviteModal = useCallback(() => {
    setUser(null);
    setIsInvite(false);
  }, []);

  useEffect(() => {
    let newFilterData = isArchivedView ? archivedData : data;

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      newFilterData = newFilterData.filter((x) =>
        SEARCH_FIELDS.some(
          (field) => x[field] && x[field].toLowerCase().includes(query)
        )
      );
    }

    // The archive keeps the server's order: most recently deleted first
    if (isArchivedView) {
      setFilteredData(newFilterData);
      return;
    }

    if (filterByStatus !== "0") {
      newFilterData = newFilterData.filter(
        (x) => x.status === Number(filterByStatus)
      );
    }

    //Sort
    function compareBy(a, b) {
      const valA = a[sortBy];
      const valB = b[sortBy];

      if (typeof valA === "string" && typeof valB === "string") {
        return valA.localeCompare(valB, undefined, { sensitivity: "base" });
      }

      if (valA < valB) {
        return -1;
      }
      if (valA > valB) {
        return 1;
      }
      return 0;
    }

    if (newFilterData.length) {
      newFilterData = newFilterData.slice().sort(compareBy);
    }

    setFilteredData(newFilterData);
  }, [filterByStatus, sortBy, searchQuery, data, archivedData, isArchivedView]);

  const counts = {
    all: data.length,
    active: data.filter((x) => x.status === 1).length,
    inactive: data.filter((x) => x.status === 2).length,
    archived: archivedData.length,
  };

  // Switching list starts fresh: page, search and filters belong to a list
  const doSetView = (newView) => {
    updateParams({
      view: newView === VIEW_OPTIONS[0] ? "" : newView,
      page: "",
      q: "",
      status: "",
    });
  };

  const printSummary = isArchivedView
    ? "Archived members"
    : `Members${
        filterByStatus === "1"
          ? " — Active"
          : filterByStatus === "2"
          ? " — Inactive"
          : ""
      }`;

  // A deletion can leave the current page empty; fall back to the last one
  const lastPage = Math.max(0, Math.ceil(filteredData.length / rowsPerPage) - 1);
  const currentPage = Math.min(page, lastPage);
  const visibleData = isPrinting
    ? filteredData
    : filteredData.slice(
        currentPage * rowsPerPage,
        currentPage * rowsPerPage + rowsPerPage
      );

  // A new filter, sort or search starts again from the first page
  const doSetFilterByStatus = (filter) => {
    updateParams(
      { status: filter === STATUS_OPTIONS[0] ? "" : filter, page: "" },
      { replace: true }
    );
  };

  const doSetSortBy = (sort) => {
    updateParams(
      { sort: sort === SORT_OPTIONS[0] ? "" : sort, page: "" },
      { replace: true }
    );
  };

  const doSetSearchQuery = (query) => {
    updateParams({ q: query, page: "" }, { replace: true });
  };

  return (
    <div>
      {isUpdate ? (
        <UserUpdateModal
          user={user}
          open={isUpdate}
          handleClose={handleCloseUpdateModal}
          doFetchCustomers={doFetchCustomers}
        />
      ) : null}

      {isCancel ? (
        <CancelModal
          user={user}
          open={isCancel}
          handleClose={handleCloseCancelModal}
          doFetchCustomers={doFetchCustomers}
        />
      ) : null}

      {isDelete ? (
        <DeleteModal
          user={user}
          open={isDelete}
          handleClose={handleCloseDeleteModal}
          doFetchCustomers={doFetchCustomers}
        />
      ) : null}

      {isInvite ? (
        <ResubscribeInviteModal
          user={user}
          open={isInvite}
          handleClose={handleCloseInviteModal}
        />
      ) : null}

      {archivedUser ? (
        <ArchivedDetailsModal
          user={archivedUser}
          open
          handleClose={() => setArchivedUser(null)}
        />
      ) : null}

      {isAdd ? (
        <AddSubscriberModal
          open={isAdd}
          handleClose={() => setIsAdd(false)}
          doFetchCustomers={doFetchCustomers}
        />
      ) : null}

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {menuRow?.status === 1 && (
          <MenuItem onClick={() => doMenuAction(setIsUpdate)}>Update</MenuItem>
        )}
        {menuRow?.status !== 2 && (
          <MenuItem onClick={() => doMenuAction(setIsCancel)}>
            Cancel membership
          </MenuItem>
        )}
        {menuRow?.status === 2 && (
          <MenuItem onClick={() => doMenuAction(setIsInvite)}>
            Send resubscribe invite
          </MenuItem>
        )}
        {menuRow?.status === 2 && (
          <MenuItem onClick={() => doMenuAction(setIsDelete)}>Delete</MenuItem>
        )}
        {menuRow?.subscription_id && (
          <MenuItem
            onClick={() => {
              navigator.clipboard
                .writeText(menuRow.subscription_id)
                .catch(() => {});
              handleCloseMenu();
            }}
          >
            Copy subscription ID
          </MenuItem>
        )}
      </Menu>

      <Box sx={{ paddingTop: 2, flexGrow: 1 }} className="shadow-lg border">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert error={error} containerClassName="mt-0 mb-2" />
            <TableContainer
              component={Paper}
              className="!border-none !shadow-none"
            >
              <Typography variant="h4" className="p-4 customer-list-header">
                Customer List
              </Typography>
              <div className="print-header">
                <h1>{printSummary}</h1>
                <p>
                  {new Date().toLocaleDateString("en-CA")} ·{" "}
                  {filteredData.length}{" "}
                  {filteredData.length === 1 ? "record" : "records"}
                  {searchQuery.trim() ? ` · search “${searchQuery.trim()}”` : ""}
                </p>
              </div>
              <Filters
                view={view}
                counts={counts}
                onViewChange={doSetView}
                filterByStatus={filterByStatus}
                sortBy={sortBy}
                searchQuery={searchQuery}
                doSetFilterByStatus={doSetFilterByStatus}
                doSetSortBy={doSetSortBy}
                doSetSearchQuery={doSetSearchQuery}
                onAddMember={() => setIsAdd(true)}
                onPrint={() => window.print()}
              />
              {isArchivedView ? (
                <Table sx={{ minWidth: 650 }} aria-label="archived members">
                  <TableHead>
                    <TableRow>
                      <TableCell className="!font-semibold">
                        Name / Email
                      </TableCell>
                      <TableCell className="!font-semibold">Phone #</TableCell>
                      <TableCell className="!font-semibold">Address</TableCell>
                      <TableCell className="!font-semibold">Deleted</TableCell>
                      <TableCell
                        align="right"
                        className="!font-semibold action-col"
                      >
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleData.length > 0 ? (
                      visibleData.map((row) => {
                        const address = formatAddress(row);
                        return (
                          <TableRow key={row.id}>
                            <TableCell size="small">
                              <div>{row.name}</div>
                              <div className="text-xs text-gray-600 break-all">
                                {(row.email || "").toLowerCase()}
                              </div>
                            </TableCell>
                            <TableCell size="small">{row.phone_number}</TableCell>
                            <TableCell size="small">
                              <div>{address.line1}</div>
                              <div>{address.line2}</div>
                            </TableCell>
                            <TableCell size="small">
                              <div>{formatDate(row.deleted_date)}</div>
                              <div className="text-xs text-gray-600">
                                {row.deleted_by}
                              </div>
                            </TableCell>
                            <TableCell
                              size="small"
                              align="right"
                              className="action-row"
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setArchivedUser(row)}
                              >
                                Details
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} size="medium">
                          <div className="text-center">
                            No archived members found.
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              ) : (
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell className="!font-semibold">
                      Name / Email
                    </TableCell>
                    <TableCell className="!font-semibold">Phone #</TableCell>
                    <TableCell className="!font-semibold status-col">
                      Status
                    </TableCell>
                    <TableCell className="!font-semibold">Address</TableCell>
                    <TableCell className="!font-semibold">Payment</TableCell>
                    <TableCell
                      align="right"
                      className="!font-semibold action-col"
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleData.length > 0 ? (
                    <>
                      {visibleData.map((row) => {
                        const address = formatAddress(row);
                        return (
                          <TableRow
                            key={row.id}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell size="small">
                              <div>{row.name}</div>
                              <div className="text-xs text-gray-600 break-all">
                                <CopyText
                                  text={(row.email || "").toLowerCase()}
                                />
                              </div>
                            </TableCell>
                            <TableCell size="small">
                              <CopyText text={row.phone_number} />
                            </TableCell>
                            <TableCell size="small" className="status-row">
                              <span
                                className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                                  row.status === 1
                                    ? "bg-green-100 text-green-800"
                                    : "bg-slate-200 text-slate-600"
                                }`}
                              >
                                {row.status_desc}
                              </span>
                            </TableCell>
                            <TableCell size="small">
                              <div>{address.line1}</div>
                              <div>{address.line2}</div>
                            </TableCell>
                            <TableCell size="small">
                              {row.subscription_id ? (
                                <Tooltip
                                  arrow
                                  title={
                                    <>
                                      <div>{row.subscription_id}</div>
                                      <div>Click to open in Stripe</div>
                                    </>
                                  }
                                >
                                  <a
                                    href={`https://dashboard.stripe.com/subscriptions/${row.subscription_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap bg-indigo-100 text-indigo-800 hover:bg-indigo-200"
                                  >
                                    Stripe
                                    <ExternalLink size={12} />
                                  </a>
                                </Tooltip>
                              ) : (
                                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap bg-amber-100 text-amber-800">
                                  Manual
                                </span>
                              )}
                            </TableCell>
                            <TableCell
                              size="small"
                              align="right"
                              className="action-row"
                            >
                              <IconButton
                                aria-label={`Actions for ${row.name}`}
                                size="small"
                                onClick={(event) => {
                                  setMenuRow(row);
                                  setMenuAnchor(event.currentTarget);
                                }}
                              >
                                <MoreVertical size={18} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <TableRow>
                        <TableCell colSpan={6} size="medium">
                          <div className="text-center">No record found.</div>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
              )}
              <div className="pagination-container">
                <TablePagination
                  component="div"
                  count={filteredData.length}
                  page={currentPage}
                  onPageChange={(event, newPage) =>
                    setPagination(newPage, rowsPerPage)
                  }
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(event) =>
                    setPagination(0, Number(event.target.value))
                  }
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
              </div>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}

export default Customer;
